import React, { useState, useEffect } from 'react';
import { Modal, Alert, Button, Typography, Space, Card } from 'antd';
import { ExclamationCircleOutlined, CrownOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAuth } from './auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, apiClient } from '../../api/config';
import { logger } from '../../utils/logger';

const { Title, Text } = Typography;

/**
 * 頁面瀏覽限制檢查器
 * @param {Object} props - 組件屬性
 * @param {boolean} props.open - 是否顯示限制提示
 * @param {Function} props.onClose - 關閉回調
 * @param {string} props.targetPage - 目標頁面名稱
 */
const PageviewLimitChecker = ({ open, onClose, targetPage = 'top_companies' }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [limitInfo, setLimitInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 檢查瀏覽限制
  const checkViewLimit = async () => {
    if (!isAuthenticated || !user?.user_id) {
      setError('用戶未登入');
      return;
    }

    setLoading(true);
    setError('');

    try {
      logger.log('檢查頁面瀏覽限制:', { targetPage, userId: user.user_id });
      
      const response = await apiClient.get(`${API_CONFIG.endpoints.TOPCOMPANIES.CHECK_VIEW_LIMIT}`, {
        params: { userId: user.user_id }
      });

      if (response.data?.success) {
        setLimitInfo(response.data.data);
        logger.log('瀏覽限制檢查結果:', response.data.data);
      } else {
        setError('檢查瀏覽限制失敗');
      }
    } catch (err) {
      logger.error('檢查瀏覽限制時出錯:', err);
      setError(err.response?.data?.message || '檢查瀏覽限制時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  // 當組件打開時檢查限制
  useEffect(() => {
    if (open && isAuthenticated) {
      checkViewLimit();
    }
  }, [open, isAuthenticated, user?.user_id]);

  // 處理升級按鈕點擊
  const handleUpgrade = () => {
    onClose();
    navigate('/prices');
  };

  // 處理關閉
  const handleClose = () => {
    setLimitInfo(null);
    setError('');
    onClose();
  };

  // 如果沒有限制信息且沒有錯誤，不顯示任何內容
  if (!limitInfo && !error && !loading) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>瀏覽限制</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          關閉
        </Button>,
        limitInfo && !limitInfo.hasAccess && (
          <Button key="upgrade" type="primary" onClick={handleUpgrade}>
            升級方案
          </Button>
        )
      ]}
      centered
      width={500}
    >
      <div style={{ padding: '20px 0' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div>正在檢查瀏覽限制...</div>
          </div>
        )}

        {error && (
          <Alert
            message="檢查失敗"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}

        {limitInfo && (
          <div>
            {limitInfo.hasAccess ? (
              <Alert
                message="可以繼續瀏覽"
                description={
                  limitInfo.limit === -1 
                    ? "Pro 用戶無瀏覽限制，可以無限瀏覽"
                    : limitInfo.message
                }
                type="success"
                showIcon
                icon={<CrownOutlined />}
                style={{ marginBottom: '20px' }}
              />
            ) : (
              <Alert
                message="已達到瀏覽限制"
                description={limitInfo.message}
                type="warning"
                showIcon
                icon={<CalendarOutlined />}
                style={{ marginBottom: '20px' }}
              />
            )}

            {limitInfo.limit !== -1 && (
              <Card size="small" style={{ marginTop: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={5} style={{ margin: 0, color: '#666' }}>
                    本月瀏覽統計
                  </Title>
                  <div style={{ marginTop: '8px' }}>
                    <Text strong style={{ fontSize: '24px', color: limitInfo.hasAccess ? '#52c41a' : '#ff4d4f' }}>
                      {limitInfo.viewDays}
                    </Text>
                    <Text style={{ marginLeft: '8px', color: '#666' }}>
                      / {limitInfo.limit} 天
                    </Text>
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {limitInfo.hasAccess 
                        ? `還可瀏覽 ${limitInfo.limit - limitInfo.viewDays} 天`
                        : '已達到每月限制'
                      }
                    </Text>
                  </div>
                </div>
              </Card>
            )}

            {!limitInfo.hasAccess && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Text type="secondary">
                  升級到 Pro 方案即可無限瀏覽所有內容
                </Text>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PageviewLimitChecker;
